import { useState } from "react";
import { deleteData, postData } from "../backend";
import { useModal } from "../contexts";
import { TeamUser, Workflow } from "../interfaces";
import { CogIcon, } from "@heroicons/react/outline";
import useWorkflow from "../hooks/useWorkflow";
import useUser from "../hooks/useUser";
import Modal from "./Modal";
import { capitalize } from "../utils";
import Avatar from "./Avatar";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SettingsTeamModalInvite from "./SettingsTeamModalInvite";

interface Props {
  workflow: Workflow;
}

function WorkflowModalShare(props: Props) {
  const history = useHistory();
  const { openModal, closeModal } = useModal();
  const { t } = useTranslation();

  const { user, hasPermission } = useUser();

  const { workflow, mutateWorkflow } = useWorkflow(props.workflow.id);
  const team = workflow?.team ?? user?.activeTeam!;

  const [isUpdatingTeam, setIsUpdatingTeam] = useState(false);
  const [isUpdatingOwner, setIsUpdatingOwner] = useState(false);

  async function shareWithTeam() {
    setIsUpdatingTeam(true);

    const workflow = await postData(`/workflows/${props.workflow.id}/team/`)
      .catch(console.error);

    setIsUpdatingTeam(false);
    mutateWorkflow(workflow);
  }

  async function removeFromTeam() {
    setIsUpdatingTeam(true);

    const workflow = await deleteData(`/workflows/${props.workflow.id}/team/`)
      .catch(console.error);

    setIsUpdatingTeam(false);
    mutateWorkflow(workflow);
  }

  async function transferOwnership(userId: string) {
    setIsUpdatingOwner(true);

    const workflow = await postData(`/workflows/${props.workflow.id}/owner/`, { userId })
      .catch(console.error);

    setIsUpdatingOwner(false);
    mutateWorkflow(workflow);
  }

  const sortedUsers: Array<TeamUser> = team.members
    .sort((a: TeamUser, b: TeamUser) => {
      if (a.id === workflow?.owner?.id) return -1;
      if (b.id === workflow?.owner?.id) return 1;

      const aStr = a.fullName.toLowerCase() + a.email.toLowerCase();
      const bStr = b.fullName.toLowerCase() + b.email.toLowerCase();

      if (aStr < bStr) return -1;
      if (aStr > bStr) return 1;
      return 0;
    });

  if (!workflow)
    return null;

  return (
    <Modal
      title={`${t("Share")} '${workflow.name}'`}
      onClose={() => closeModal()}
      size="xl"
    >
      <div className="mt-2">
        {workflow.team &&
          <div className="flex flex-col text-sm font-semibold gap-4">
            <div className="flex gap-2">
              <div className="text-green">{t("Workflow Shared")}</div>
              <div className="text-gray-400">|</div>
              <button className="text-gray-400 hover:text-red" onClick={() => removeFromTeam()}>
                {isUpdatingTeam ? t("Removing...") : t("Remove")}
              </button>
            </div>
            <div className="text-gray-600 -mt-2">
              {t("All members of you team will be able to see this workflow. By default the workflow will be processed using the owner's account.")}
            </div>
          </div>
        }
        {!workflow.team &&
          <div className="text-gray-600 text-sm font-semibold">
            {t("By sharing this workflow all members in your team will be able to access the source and template files along with the the output folder.")}
          </div>
        }
      </div>

      <div className="w-full mt-4 scrollbar-gutter">
        <table className="table-auto w-full">
          <thead>
            <tr className="text-sm font-gilroy text-gray-600 text-left border-b">
              <th className="text-left w-auto">
                <div className="flex items-center gap-1 cursor-pointer select-none">
                  {t("Team Members")}
                  <CogIcon className="w-4 h-4 flex-shrink-0 text-gray-400 hover:text-gray-600 -mt-1" onClick={() => { history.push("/settings/team"); closeModal() }} />
                </div>
              </th>
              <th className="text-left w-[100px]">
                <div className="flex items-center gap-1 cursor-pointer select-none">
                  {/* {t("Ownership")} */}
                </div>
              </th>
              <th className="text-left w-[200px]">
                <div className="text-center gap-1 cursor-pointer select-none">
                  {t("Role")}
                </div>
              </th>
            </tr>
          </thead>
        </table>
      </div>

      <div className="overflow-y-scroll max-h-[300px] scrollbar-visible">
        <table className="table-auto w-full">
          <tbody>
            {sortedUsers.map(teamUser =>
              <tr key={teamUser.id}>
                <td className="w-auto">
                  <div className="flex items-center gap-2 text-sm my-2">
                    <Avatar url={teamUser.avatar} className="h-10 w-10 flex-shrink-0" />
                    <div className="flex flex-col">
                      <div className="font-gilroy font-bold">{teamUser.fullName}</div>
                      <div className="text-gray-400">{teamUser.email}</div>
                    </div>
                  </div>
                </td>
                <td className="w-[100px]">
                  {(teamUser.id !== workflow?.owner?.id && !isUpdatingOwner && (hasPermission(workflow.team, "teams.admin") || workflow.owner?.id === user?.id)) &&
                    <div className="font-semibold font-gilroy text-gray-400 text-sm underline hover:text-blue cursor-pointer" onClick={() => transferOwnership(teamUser.id)}>
                      {t("Make owner")}
                    </div>
                  }
                  {isUpdatingOwner &&
                    <div className="font-semibold font-gilroy text-gray-400 text-sm">
                      Updating...
                    </div>
                  }
                </td>
                <td className="w-[200px] text-sm text-center">
                  <div className="font-semibold font-gilroy text-gray-600">
                    {teamUser.id === workflow?.owner?.id
                      ? t("Workflow Owner")
                      : (
                        teamUser.teamRole === "OWNER"
                          ? t("Team Owner")
                          : capitalize(teamUser.teamRole)
                      )
                    }
                  </div>
                </td>
              </tr>
            )}
            {sortedUsers.length === 0 &&
              <tr className="w-full">
                <td className="flex justify-center items-center py-8" colSpan={5}>
                  <div className="font-semibold text-gray-400">
                    {t("No results")}
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <div className="w-full scrollbar-gutter border-t pt-4 mt-auto flex items-center justify-between">
        <button className="btn btn-gray" onClick={() => closeModal()} type="button">{t("Cancel")}</button>
        {!workflow.team &&
          <button className="btn btn-blue w-52 flex-shrink-0" onClick={shareWithTeam} disabled={isUpdatingTeam}>
            {isUpdatingTeam ? t("Sharing...") : t("Share with Team")}
          </button>
        }
        {workflow.team &&
          <button className="btn btn-blue w-52 flex-shrink-0" onClick={() => openModal(<SettingsTeamModalInvite />)} disabled={!hasPermission(user?.activeTeam, "teams.admin")}>
            {t("Invite")}
          </button>
        }
      </div>
    </Modal>
  )

}

export default WorkflowModalShare;
